import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
    setDeviceUniqueID,
    setName,
    setAddressOne,
    setAddressTwo,
    setCity,
    setUserState,
    setCountry,
    setPincode,
    setEmail,
    setMobileNumber,
    setPassword,
    setStateList,
    setCountryList,
} from '@store/reducers/auth/signupSlice';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { MdInfo } from 'react-icons/md';
import { fetchCountries, fetchCountryId, fetchStates } from '@utils/utils';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import {
    MOBILE_NUMBER_PATTERN,
    MOBILE_NUMBER_TITLE,
    PASSWORD_MIN_Length,
    PASSWORD_MAX_Length,
    PASSWORD_PATTERN,
    PASSWORD_TITLE,
    PINCODE_MAXIMUM_LENGTH,
    PINCODE_MINIMUM_LENGTH,
    PINCODE_PATTERN,
    PINCODE_TITLE,
    MOBILE_NUMBER_MIN_LENGTH,
    MOBILE_NUMBER_MAX_LENGTH,
    NAME_TEXT_MIN_LENGTH,
    NAME_TEXT_MAX_LENGTH,
    NAME_TEXT_PATTERN,
    NAME_TEXT_TITLE,
    DEVICE_NUMBER_MIN_LENGTH,
    DEVICE_NUMBER_MAX_LENGTH,
    DEVICE_NUMBER_TITLE,
    ADDRESS_MAX_LENGTH,
    DEVICE_NUMBER_PATTERN,
    EMAIL_PATTERN,
    EMAIL_TITLE,
    EMAIL_MAX_Length,
} from '@constants/form';

const IndividualForm = () => {
    // const [countryList, setCountryList] = useState<
    //     { id: string; code: string; name: string }[]
    // >([]);
    // const [stateList, setStateList] = useState<{ id: string; name: string }[]>(
    //     []
    // );
    const countryList = useAppSelector((state) => state.signup.countryList);
    const stateList = useAppSelector((state) => state.signup.stateList);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const deviceUniqueID = useAppSelector(
        (state) => state.signup.deviceUniqueID
    );
    const [nameError, setNameError] = useState('');
    const name = useAppSelector((state) => state.signup.name);
    const address_1 = useAppSelector((state) => state.signup.address_one);
    const address_2 = useAppSelector((state) => state.signup.address_two);
    const city = useAppSelector((state) => state.signup.city);
    const state = useAppSelector((state) => state.signup.userState);
    const country = useAppSelector((state) => state.signup.country);
    const pincode = useAppSelector((state) => state.signup.pincode);
    const email = useAppSelector((state) => state.signup.email);
    const mobileNumber = useAppSelector((state) => state.signup.mobileNumber);
    const password = useAppSelector((state) => state.signup.password);
    const adminCreate = useAppSelector(
        (state) => state.signup.adminCreateIndividual
    );
    const visible = useAppSelector((state) => state.signup.showPassword);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        // Only fetch if country list is empty and we haven't fetched yet
        // This prevents duplicate fetches in React.StrictMode (development)
        if (countryList.length === 0 && !hasFetchedRef.current) {
            hasFetchedRef.current = true;
            handleFetchCountryList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch states on load if country has a value (for edit pages, not signup)
    useEffect(() => {
        const fetchStatesForCountry = async () => {
            if (country && countryList.length > 0 && stateList.length === 0) {
                const selectedCountry = countryList.find(
                    (c) => c.name === country
                );
                if (selectedCountry) {
                    await handleFetchStates(selectedCountry.code);
                }
            }
        };
        fetchStatesForCountry();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, countryList]);

    const handleFetchCountryList = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            console.error('Error fetching countries:', list.error);
        }
    };

    const handleFetchStates = async (countryId: string) => {
        const result = await fetchStates(countryId);
        if (result.states) {
            dispatch(setStateList(result.states));
        } else if (result.error) {
            // Handle error message
            console.error('Error fetching states:', result.error);
        }
    };

    const handleDeviceUniqueIDChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        // dispatch(setDeviceUniqueID(event.target.value));
        const value = event.target.value;
        if (/^\d*$/.test(value)) {
            dispatch(setDeviceUniqueID(value));
        }
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const regex = /^[A-Za-z\s]*$/;
        if (regex.test(value)) {
            dispatch(setName(value));
        }
    };

    const handleAddressOneChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setAddressOne(event.target.value));
    };

    const handleAddressTwoChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setAddressTwo(event.target.value));
    };

    // const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     dispatch(setCity(event.target.value));
    // };
    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        // Only allow alphabetic characters (letters) using regex
        const sanitizedValue = inputValue.replace(/[^A-Za-z]/g, '');
        dispatch(setCity(sanitizedValue));
    };

    const handleStateChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        dispatch(setUserState(event.target.value));
    };

    const handleCountryChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const selectedCountry = countryList.find(
            (c) => c.name === event.target.value
        );
        if (selectedCountry) {
            dispatch(setCountry(selectedCountry.name));
            dispatch(setUserState('')); // Clear state when country changes
            dispatch(setStateList([])); // Clear state list
            handleFetchStates(selectedCountry.code); // Fetch states for the selected country
        } else {
            dispatch(setCountry(event.target.value));
            dispatch(setUserState('')); // Clear state when country changes
            dispatch(setStateList([])); // Clear state list
        }
    };

    const handlePincodeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow only numbers (0-9)
        if (/^\d*$/.test(value) && value.length <= PINCODE_MAXIMUM_LENGTH) {
            dispatch(setPincode(value));
        }
        // dispatch(setPincode(event.target.value));
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setEmail(event.target.value));
    };

    const handleMobileNumberChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow only numbers (0-9)
        if (/^\d*$/.test(value) && value.length <= MOBILE_NUMBER_MAX_LENGTH) {
            dispatch(setMobileNumber(value));
        }

        // dispatch(setMobileNumber(event.target.value));
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setPassword(event.target.value));
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    return (
        <>
            <div className="flex justify-start">
                {/* <div className="w-2/4 mr-8">
                    <label htmlFor="device_unique_id" className="mb-2">
                        <div className="text-sm font-medium text-light_grey flex items-center">
                            {t('auth.signup.device_unique_id')}
                            <span className="text-red ml-2">*</span>
                            <MdInfo className="text-secondary ml-2 text-base" />
                        </div>
                    </label>
                    <input
                        type="text"
                        name="device_unique_id"
                        id="device_unique_id"
                        value={deviceUniqueID}
                        onChange={handleDeviceUniqueIDChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.device_unique_id_placeholder'
                        )}
                        pattern={DEVICE_NUMBER_PATTERN}
                        title={DEVICE_NUMBER_TITLE}
                        minLength={DEVICE_NUMBER_MIN_LENGTH}
                        maxLength={DEVICE_NUMBER_MAX_LENGTH}
                        required
                    />
                </div> */}
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="name"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.name_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        id="name"
                        value={name}
                        onChange={handleNameChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.name_placeholder')}
                        minLength={NAME_TEXT_MIN_LENGTH}
                        maxLength={NAME_TEXT_MAX_LENGTH}
                        title={NAME_TEXT_TITLE}
                        pattern={NAME_TEXT_PATTERN}
                        required
                    />
                </div>
                <div className="w-2/4">
                    <label
                        htmlFor="address_1"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.address_1_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="address_1"
                        id="address_1"
                        value={address_1}
                        onChange={handleAddressOneChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.address_1_palceholder')}
                        maxLength={ADDRESS_MAX_LENGTH}
                        required
                    />
                </div>
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="address_2"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.address_2_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="address_2"
                        id="address_2"
                        value={address_2}
                        onChange={handleAddressTwoChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.address_2_palceholder')}
                        maxLength={ADDRESS_MAX_LENGTH}
                        required
                    />
                </div>
                <div className="w-2/4">
                    <label
                        htmlFor="city"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.city_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="city"
                        id="city"
                        value={city}
                        onChange={handleCityChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.city_placeholder')}
                        maxLength={NAME_TEXT_MAX_LENGTH}
                        required
                    />
                </div>
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="country"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.country_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <select
                        name="country"
                        id="country"
                        value={country}
                        onChange={handleCountryChange}
                        className="w-full px-3 py-[0.65rem] border rounded !placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                        required
                    >
                        <option value="" disabled>
                            {t('auth.signup.country_placeholder')}
                        </option>
                        {countryList.map((country) => (
                            <option key={country.code} value={country.name}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="w-2/4">
                    <label
                        htmlFor="state"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.state_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <select
                        name="state"
                        id="state"
                        value={state}
                        onChange={handleStateChange}
                        className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                        required
                    >
                        <option value="" disabled>
                            {t('auth.signup.state_placeholder')}
                        </option>
                        {stateList.map((state) => (
                            <option key={state.id} value={state.name}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="pincode"
                        className=" mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.pincode_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="pincode"
                        id="pincode"
                        value={pincode}
                        onChange={handlePincodeChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.pincode_placeholder')}
                        minLength={PINCODE_MINIMUM_LENGTH}
                        maxLength={PINCODE_MAXIMUM_LENGTH}
                        pattern={PINCODE_PATTERN}
                        title={PINCODE_TITLE}
                        required
                    />
                </div>
                {adminCreate &&(
                    <div className="w-2/4">
                        <label
                            htmlFor="mobile_number"
                            className="mb-2 text-sm font-medium text-light_grey"
                        >
                            {t('auth.signup.mobile_number_label')}
                            <span className="text-red ml-2">*</span>
                        </label>
                        <input
                            type="text"
                            name="mobile_number"
                            id="mobile_number"
                            value={mobileNumber}
                            onChange={handleMobileNumberChange}
                            className="form-control px-3 py-2 placeholder-gains_boro"
                            placeholder={t(
                                'auth.signup.mobile_number_placeholder'
                            )}
                            minLength={MOBILE_NUMBER_MIN_LENGTH}
                            maxLength={MOBILE_NUMBER_MAX_LENGTH}
                            pattern={MOBILE_NUMBER_PATTERN}
                            title={MOBILE_NUMBER_TITLE}
                            required
                        />
                    </div>
                )}
            </div>
            {adminCreate && (
                <>
                    <div className="flex justify-start mt-4">
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="email"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.email_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            {visible ? (
                                <input
                                    type="email"
                                    name="email"
                                    id="signup_email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_placeholder'
                                    )}
                                    pattern={EMAIL_PATTERN}
                                    title={EMAIL_TITLE}
                                    maxLength={EMAIL_MAX_Length}
                                    required
                                    autoComplete="off"
                                />
                            ) : (
                                <input
                                    type="email"
                                    name="email"
                                    value={email}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_placeholder'
                                    )}
                                    readOnly
                                    disabled
                                    autoComplete="off"
                                />
                            )}
                        </div>
                        {visible && (
                            // <div className="flex justify-start mt-4">
                                <div className="relative w-2/4">
                                    <label
                                        htmlFor="password"
                                        className="mb-2 text-sm font-medium text-light_grey"
                                    >
                                        {t('auth.signup.password_label')}
                                    </label>
                                    <div className="relative flex items-center rounded-md">
                                        <input
                                            type={
                                                showPassword ? 'text' : 'password'
                                            }
                                            name="password"
                                            id="signup_password"
                                            value={password}
                                            onChange={handlePasswordChange}
                                            className="form-control px-3 py-2 placeholder-gains_boro"
                                            placeholder={t(
                                                'auth.signup.password_placeholder'
                                            )}
                                            minLength={PASSWORD_MIN_Length}
                                            maxLength={PASSWORD_MAX_Length}
                                            pattern={PASSWORD_PATTERN}
                                            title={PASSWORD_TITLE}
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 cursor-pointer"
                                            onClick={handleTogglePasswordVisibility}
                                        >
                                            {showPassword ? (
                                                <IoEyeOutline className="text-secondary" />
                                            ) : (
                                                <IoEyeOffOutline className="text-secondary" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                // <div className="w-2/4"></div>
                            // </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default IndividualForm;
